import axios from "axios";
import { store } from "../redux/store";
import { logout } from "../redux/auth/authSlice";
import { showToast } from "../utils/toast";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  // timeout: 120,
  headers: {
    "Content-Type": "application/json",
  },
});


/* REQUEST INTERCEPTOR from Redux*/
api.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state?.auth?.access_token;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if(timezone) {
      config.headers["X-TIMEZONE"] = timezone;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* Helper: flatten error object into readable string */
const formatErrorDetails = (error) => {
  if (!error) return null;

  // String error
  if (typeof error === "string") return error;

  // Array of strings
  if (Array.isArray(error)) return error.join(", ");

  // Object: { field: ["msg1", "msg2"], ... }
  if (typeof error === "object") {
    const lines = Object.entries(error).map(([field, val]) => {
      const msgs = Array.isArray(val) ? val.join(", ") : String(val);
      // Capitalize field name and replace underscores
      const label = field.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
      return `${label}: ${msgs}`;
    });
    return lines.join("\n");
  }

  return null;
};

/* RESPONSE INTERCEPTOR */
api.interceptors.response.use(
  (response) => {
    const skipToast = response.config?.skipToast;

    if (!skipToast && response?.data?.message) {
      showToast(response.data.message, "success");
    }

    return response;
  },
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;
    const message = data?.message || "Something went wrong";
    const errors = data?.error || null;

    const skipToast = error.config?.skipToast;

    if (status === 401) {
      showToast(message, "error");
      store.dispatch(logout());
      window.location.href = "/";
      return Promise.reject(error);
    }

    if (!skipToast) {
      if (status === 403) {
        showToast(message, "warning");
      } else if (status >= 500) {
        showToast(message, "error");
      } else {
        if (errors) {
          showToast(message, "error", errors);
        } else {
          showToast(message, "error")
        }
      }
    }

    return Promise.reject(error);
  }
);

/* API METHODS */
const service = {
  get: (url, params = {}, config = {}) =>
    api.get(url, { params, ...config }),

  post: (url, data = {}, config = {}) =>
    api.post(url, data, config),

  put: (url, data = {}, config = {}) =>
    api.put(url, data, config),

  patch: (url, data = {}, config = {}) =>
    api.patch(url, data, config),

  delete: (url, config = {}) =>
    api.delete(url, config),

  upload: (url, data, config = {}) =>
    api.post(url, data, {
      headers: { "Content-Type": "multipart/form-data" },
      ...config,
    }),

  uploadPut: (url, data, config = {}) =>
    api.put(url, data, {
      headers: { "Content-Type": "multipart/form-data" },
      ...config,
    }),
};

export default service;